/**
 * BullseyeTab — Search Detail's Bullseye tab (epic 2026-10-05-bullseye,
 * sub-task 03): the search's applied AI Review lists on one target, one slice
 * per list, so a recruiter can share their screen on a pitch call.
 *
 * What is saved and what is not:
 *   - the ring mode and the score boundaries are the SEARCH's, shared by the
 *     whole team and saved through `useBullseyeSettings` (debounced, re-read
 *     before every save);
 *   - which lists are on is this browser session's (sessionStorage, per
 *     search);
 *   - Present mode, the focused slice and how non-fits are shown are this
 *     view's only, and reset when the tab is left.
 *
 * The maths is in `bullseyeGeometry.ts`; this component wires state to it.
 * The approved prototype is https://claude.ai/artifact/1VgSErKWjSSGACc4LxT2Tx.
 */
import type { BullseyeList } from "@nu-assist/contracts";
import { Check, Presentation } from "lucide-react";
import { useCallback, useDeferredValue, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import type { AvatarsClient } from "../../../../clients/avatars.client";
import type { BullseyeClient } from "../../../../clients/bullseye.client";
import { RetryButton } from "../../../../components/RetryButton";
import { displayTitle } from "../../../research/import-detail/importTitle";
import { Legend, ListsPanel } from "./BullseyeSidePanel";
import { BullseyeTarget, type PlottedList } from "./BullseyeTarget";
import { RingBoundaries } from "./RingBoundaries";
import {
  DEFAULT_BOUNDS,
  type RingMode,
  clampBound,
  evenBounds,
  histogram,
  layoutBullseye,
  ringModel,
} from "./bullseyeGeometry";
import { listTone } from "./bullseyeTones";
import { useBullseyeData, useBullseyeSettings } from "./useBullseye";

/** The page id of the Bullseye tab in the flag registry and the preview ladder. */
export const BULLSEYE_PAGE_ID = "bullseye";

export interface BullseyeTabProps {
  client: BullseyeClient;
  /** Presigns a hovered person's photo when CloudFront does not serve it. */
  avatarsClient: AvatarsClient;
  searchId: string;
  /** Where "People Lists" points from the empty state (this search's People Lists tab). */
  peopleListsHref: string;
  /**
   * An AI Review run to show ALONE: the People List detail's Bullseye
   * shortcut (`?review=<ai_review_id>`). Once the lists load, every other
   * list is switched off (saved like a manual toggle, so the reader can turn
   * them back on) and `onReviewApplied` is called so the page can drop the
   * parameter. An id that is not among the search's lists changes nothing.
   */
  onlyReviewId?: string | null;
  onReviewApplied?: () => void;
}

const offKey = (searchId: string) => `nu_bullseye_off_${searchId}`;

function readOff(searchId: string): Set<string> {
  try {
    const raw = sessionStorage.getItem(offKey(searchId));
    const ids: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(ids) ? ids.filter((x): x is string => typeof x === "string") : []);
  } catch {
    return new Set();
  }
}

function writeOff(searchId: string, off: ReadonlySet<string>): void {
  try {
    sessionStorage.setItem(offKey(searchId), JSON.stringify([...off]));
  } catch {
    // Storage blocked: the toggle still works for as long as the tab is open.
  }
}

const CHIP_BASE =
  "rounded-pill border px-3 py-1 text-label focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent";
const chipClass = (on: boolean) =>
  `${CHIP_BASE} ${
    on
      ? "border-accent bg-accent-muted text-accent-ink"
      : "border-border bg-surface text-text hover:border-border-hover"
  }`;

const MODE_NOTE: Record<RingMode, string> = {
  ai_score:
    "Rings are bands of the weighted AI score. The closer a dot is to the centre, the higher the person scored.",
  criteria_met:
    "One ring per criterion a person can miss. A criterion counts only when the AI scored it 3. Where a dot sits inside its ring means nothing.",
};

export function BullseyeTab({
  client,
  avatarsClient,
  searchId,
  peopleListsHref,
  onlyReviewId = null,
  onReviewApplied,
}: BullseyeTabProps) {
  const data = useBullseyeData(client, searchId);
  const settings = useBullseyeSettings(client, searchId);
  const [present, setPresent] = useState(false);
  const [outside, setOutside] = useState(false);
  const [focus, setFocus] = useState<string | null>(null);
  const [off, setOff] = useState<Set<string>>(() => readOff(searchId));

  const all: PlottedList[] = useMemo(
    () =>
      (data.data?.lists ?? []).map((list, i) => {
        const title = displayTitle(list.list_name) || list.list_name;
        // The layout wraps `list_name` for the label, so it gets the display name.
        return { list: { ...list, list_name: title }, title, tone: listTone(i) };
      }),
    [data.data]
  );
  useEffect(() => {
    if (onlyReviewId === null || !data.data) return;
    const ids = data.data.lists.map((l) => l.ai_review_id);
    if (ids.includes(onlyReviewId)) {
      const next = new Set(ids.filter((id) => id !== onlyReviewId));
      setOff(next);
      writeOff(searchId, next);
      setFocus(null);
    }
    onReviewApplied?.();
  }, [onlyReviewId, data.data, searchId, onReviewApplied]);

  const active = useMemo(() => all.filter((p) => !off.has(p.list.ai_review_id)), [all, off]);
  const activeLists = useMemo(() => active.map((p) => p.list), [active]);

  const mode = settings.ringMode;
  // The slider moves `ringBounds` on every input event; the target re-lays out
  // behind it at React's pace instead of blocking the drag.
  const bounds = useDeferredValue(settings.ringBounds);
  const model = useMemo(() => ringModel(mode, bounds, activeLists), [mode, bounds, activeLists]);
  const layout = useMemo(
    () => layoutBullseye(activeLists, model, { outside }),
    [activeLists, model, outside]
  );
  const bins = useMemo(
    () =>
      histogram(
        activeLists.flatMap((l) => l.people.map((p) => p.weighted_total)),
        settings.ringBounds
      ),
    [activeLists, settings.ringBounds]
  );

  const plottedOf = useCallback(
    (list: BullseyeList) => list.people.filter((p) => model.bandOf(p, list) !== null).length,
    [model]
  );

  const toggleList = (listId: string, on: boolean) => {
    const next = new Set(off);
    if (on) next.delete(listId);
    else next.add(listId);
    setOff(next);
    writeOff(searchId, next);
    if (!on && focus === listId) setFocus(null);
  };

  const setMode = (next: RingMode) => {
    if (next === mode) return;
    setFocus(null);
    settings.update({ ring_mode: next });
  };

  if (data.isLoading || settings.isLoading) {
    return (
      <p className="text-body text-text-secondary" data-testid="bullseye-loading">
        Loading the Bullseye…
      </p>
    );
  }

  // Only when nothing has loaded: a failed background re-read (a focus
  // refetch) keeps the tab on screen with the data it already has.
  if ((data.error && !data.data) || (settings.loadError && !settings.saved)) {
    return (
      <div
        className="flex flex-wrap items-center gap-3 rounded-md border border-error-strong bg-error-muted px-4 py-3 text-body text-error-ink"
        data-testid="bullseye-load-error"
      >
        <span>The Bullseye could not be loaded.</span>
        <RetryButton
          tone="error"
          busy={data.isFetching || settings.isFetching}
          onClick={() => {
            // Either read can be the one that failed: retry both.
            void data.refetch();
            settings.reload();
          }}
        />
      </div>
    );
  }

  if (all.length === 0) {
    return (
      <div
        className="rounded-md border border-border bg-surface px-4 py-6 text-body text-text-secondary"
        data-testid="bullseye-empty"
      >
        No AI Review has been applied to a list of this search yet.{" "}
        <Link to={peopleListsHref} className="text-accent-ink underline">
          People Lists
        </Link>
      </div>
    );
  }

  const pool = activeLists.reduce((n, l) => n + l.people.length, 0);
  const onTarget = pool - layout.nonfitTotal;

  return (
    <div
      className="flex flex-col gap-3"
      data-testid="bullseye-tab"
      data-mode={mode}
      data-present={present}
    >
      <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2.5">
        <h2 className="flex items-center gap-1.5 text-h2 text-text">
          Bullseye
          <span className="text-body font-normal text-text-secondary" data-testid="bullseye-count">
            ({active.length} of {all.length} lists)
          </span>
        </h2>
        <span className="inline-flex flex-wrap items-center gap-1.5 text-label text-text-secondary">
          Rings:
          <button
            type="button"
            aria-pressed={mode === "ai_score"}
            className={chipClass(mode === "ai_score")}
            onClick={() => setMode("ai_score")}
            data-testid="bullseye-mode-score"
          >
            AI score
          </button>
          <button
            type="button"
            aria-pressed={mode === "criteria_met"}
            className={chipClass(mode === "criteria_met")}
            onClick={() => setMode("criteria_met")}
            data-testid="bullseye-mode-criteria"
          >
            Criteria met
          </button>
        </span>
        <span className="inline-flex flex-wrap items-center gap-1.5 text-label text-text-secondary">
          Non-fits:
          <button
            type="button"
            aria-pressed={!outside}
            className={chipClass(!outside)}
            onClick={() => setOutside(false)}
            data-testid="bullseye-nonfit-count"
          >
            Count only
          </button>
          <button
            type="button"
            aria-pressed={outside}
            className={chipClass(outside)}
            onClick={() => setOutside(true)}
            data-testid="bullseye-nonfit-outside"
          >
            Outside the target
          </button>
        </span>
        <SaveIndicator status={settings.status} />
        <span className="ml-auto inline-flex items-center gap-3">
          {present && (
            <span
              className="text-label font-medium text-accent-ink"
              data-testid="bullseye-present-note"
            >
              Reviewers, names and hover details are hidden
            </span>
          )}
          <button
            type="button"
            aria-pressed={present}
            onClick={() => setPresent((p) => !p)}
            className={`inline-flex items-center gap-2 rounded-sm border px-3 py-1.5 text-label font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
              present
                ? "border-accent bg-accent text-bg"
                : "border-border bg-surface text-text hover:border-border-hover"
            }`}
            data-testid="bullseye-present"
          >
            <Presentation className="h-4 w-4" aria-hidden="true" />
            {present ? "Exit present mode" : "Present mode"}
          </button>
        </span>
      </div>
      <p className="max-w-3xl text-body text-text-secondary" data-testid="bullseye-mode-note">
        {MODE_NOTE[mode]}
      </p>

      {settings.saveError && (
        <div
          role="alert"
          className="flex flex-wrap items-center gap-3 rounded-md border border-error-strong bg-error-muted px-4 py-2.5 text-body text-error-ink"
          data-testid="bullseye-save-error"
        >
          <span>The ring settings were not saved: {settings.saveError.message}</span>
          <RetryButton
            tone="error"
            busy={settings.status === "saving"}
            busyLabel="Saving…"
            onClick={settings.retry}
          />
        </div>
      )}

      {settings.conflict && (
        <div
          aria-live="polite"
          className="rounded-md border border-warning-strong bg-warning-muted px-4 py-2.5 text-body text-warning-ink"
          data-testid="bullseye-conflict"
        >
          {settings.conflict}
        </div>
      )}

      <section className="flex flex-col items-start gap-4 xl:flex-row">
        <div className="grid w-full gap-4 md:grid-cols-2 xl:w-72 xl:shrink-0 xl:grid-cols-1">
          <ListsPanel
            lists={all}
            isOn={(id) => !off.has(id)}
            onToggle={toggleList}
            plotted={plottedOf}
            present={present}
          />
          {mode === "ai_score" && (
            <RingBoundaries
              bounds={settings.ringBounds}
              bins={bins}
              saved={settings.saved}
              present={present}
              boundErrors={settings.saveError?.boundErrors ?? new Map()}
              onRingCount={(k) => settings.update({ ring_bounds: evenBounds(k) })}
              onBound={(i, v) =>
                settings.update({ ring_bounds: clampBound(settings.ringBounds, i, v) })
              }
              onReset={() => settings.update({ ring_bounds: [...DEFAULT_BOUNDS] })}
            />
          )}
          <Legend model={model} ringCounts={layout.ringCounts} nonfitTotal={layout.nonfitTotal} />
        </div>
        <div className="w-full min-w-0 flex-1">
          <BullseyeTarget
            lists={active}
            model={model}
            layout={layout}
            focus={focus}
            onToggleFocus={(id) => setFocus((f) => (f === id ? null : id))}
            present={present}
            presignPhotos={avatarsClient.presignAvatars}
            footer={
              active.length ? (
                <>
                  <b className="tabular-nums text-text">{onTarget}</b> on the target ·{" "}
                  <b className="tabular-nums text-text">{layout.nonfitTotal}</b> more reviewed, not
                  a fit
                  {outside && layout.outsideShown < layout.nonfitTotal
                    ? ` (${layout.outsideShown} fit around the target)`
                    : ""}
                </>
              ) : null
            }
          />
        </div>
      </section>
    </div>
  );
}

function SaveIndicator({ status }: { status: ReturnType<typeof useBullseyeSettings>["status"] }) {
  if (status === "saving" || status === "pending") {
    return (
      <span className="text-caption text-text-muted" data-testid="bullseye-save-status">
        Saving…
      </span>
    );
  }
  if (status === "saved") {
    return (
      <span
        className="inline-flex items-center gap-1 text-caption text-text-muted"
        data-testid="bullseye-save-status"
      >
        <Check className="h-3.5 w-3.5" aria-hidden="true" />
        Saved
      </span>
    );
  }
  return null;
}
